import { useState, type ReactNode } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import type { ManufacturerPaymentMethod } from "@/api/hooks";
import type { components } from "@/api/schema";
import {
  useReplaceManufacturerFeeRules,
  useUpdateManufacturer,
  useUpdateManufacturerCaseTerms,
  type ManufacturerCaseTerms,
  type ManufacturerFeeRuleWrite,
} from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { LoadingButton } from "@/components/loading-button";
import { POInlineEditableField } from "@/components/po-inline-edit";
import { ReviewChoiceGroup } from "@/components/review-choice-group";
import { SettingCell } from "@/components/setting-cell";
import {
  InlineFieldGroup,
  type InlineGroupField,
} from "@/components/inline-field-group";
import {
  PaymentTermsField,
  type TermsMutation,
} from "@/features/manufacturers/payment-terms-panel";
import {
  describeFeeRule,
  describeOrderFees,
} from "@/features/manufacturers/source-glance";
import { cn, formatMoney, formatPercent } from "@/lib/utils";

// asyncpg returns whole-number PostgreSQL numerics as Decimal('2E+4'), which
// Pydantic serializes verbatim. Round-trip through Number to get plain decimal.
function formatSpecCreditMinimum(val: string | null | undefined): string {
  if (val == null || val === "") return "";
  const n = Number(val);
  return Number.isFinite(n) ? String(n) : val;
}

const PAYMENT_METHOD_OPTIONS = [
  { value: "ach", label: "ACH" },
  { value: "card", label: "Card" },
  { value: "check", label: "Check" },
  { value: "wire", label: "Wire" },
  { value: "portal", label: "Portal" },
] as const;

const FREIGHT_BOOKING_OPTIONS = [
  { value: "auto", label: "Auto" },
  { value: "manufacturer", label: "Manufacturer books" },
  { value: "usa_rs", label: "We book" },
] as const;

type OrderFeePolicy = "none" | "fixed_fee" | "top_up" | "hard_minimum";
type MoneyValue = string | number | null | undefined;

const ORDER_FEE_POLICY_OPTIONS: Array<{
  value: OrderFeePolicy;
  label: string;
}> = [
  { value: "none", label: "None" },
  { value: "fixed_fee", label: "Fixed fee" },
  { value: "top_up", label: "Top up to minimum" },
  { value: "hard_minimum", label: "Hard minimum" },
];

function orderFeePolicy(value: string | null | undefined): OrderFeePolicy {
  return ORDER_FEE_POLICY_OPTIONS.some((option) => option.value === value)
    ? (value as OrderFeePolicy)
    : "none";
}

function moneyInput(value: MoneyValue): string {
  if (value == null || value === "") return "";
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : String(value);
}

function normalizedMoney(value: string): string {
  return value.replace(/,/g, "").trim();
}

function parsedMoney(value: string): number | null {
  const normalized = normalizedMoney(value);
  return normalized === "" ? null : Number(normalized);
}

export type FeeRule = components["schemas"]["ManufacturerFeeRuleRead"];

function errorDetail(error: unknown, fallback: string): string {
  return error &&
    typeof error === "object" &&
    "detail" in error &&
    typeof error.detail === "string"
    ? error.detail
    : fallback;
}

/** The broken-case fee in a few words: "Broken-case fee $15.00 (max $30.00/order)". */
function describeBrokenCaseFee(caseTerms: ManufacturerCaseTerms): string | null {
  if (caseTerms.full_cases_only || caseTerms.broken_case_fee == null) return null;
  const fee = `Broken-case fee ${formatMoney(caseTerms.broken_case_fee)}`;
  return caseTerms.broken_case_fee_max == null
    ? fee
    : `${fee} (max ${formatMoney(caseTerms.broken_case_fee_max)}/order)`;
}

/** "Broken-case discount 50/10/5/10" (USA-1510), shown while the store sells below the case. */
function describeBrokenCaseDiscount(caseTerms: ManufacturerCaseTerms): string | null {
  if (caseTerms.full_cases_only || !caseTerms.broken_case_discount) return null;
  return `Broken-case discount ${caseTerms.broken_case_discount}`;
}

/** A discount chain as suppliers write it: "50/10/5/10", each step 0–100. */
function discountChainValid(value: string): boolean {
  const steps = value.split("/").map((step) => step.trim());
  return steps.every(
    (step) => /^\d+(\.\d+)?$/.test(step) && Number(step) <= 100,
  );
}

function normalizedChain(value: string): string | null {
  const chain = value
    .split("/")
    .map((step) => step.trim())
    .join("/");
  return chain === "" ? null : chain;
}

type FeeRuleKey =
  | "label"
  | "amount"
  | "kind"
  | "orderUnder"
  | "minLength"
  | "maxLength";
type FeeRuleDraft = Record<FeeRuleKey, string>;

const FEE_RULE_FIELDS: ReadonlyArray<InlineGroupField<FeeRuleKey>> = [
  { key: "label", label: "Fee name", required: true, className: "col-span-4" },
  {
    key: "amount",
    label: "Fee",
    required: true,
    inputMode: "decimal",
    className: "col-span-2",
  },
  {
    key: "kind",
    label: "When it applies",
    kind: "select",
    options: [
      { value: "order", label: "Orders under" },
      { value: "length", label: "Item length" },
    ],
    className: "col-span-3",
  },
  {
    key: "orderUnder",
    label: "Orders under",
    required: true,
    inputMode: "decimal",
    className: "col-span-3",
    hidden: (draft) => draft.kind !== "order",
  },
  {
    key: "minLength",
    label: "Shortest",
    inputMode: "decimal",
    suffix: "in",
    className: "col-span-3 sm:col-span-1",
    hidden: (draft) => draft.kind !== "length",
  },
  {
    key: "maxLength",
    label: "Longest",
    inputMode: "decimal",
    suffix: "in",
    className: "col-span-3 sm:col-span-2",
    hidden: (draft) => draft.kind !== "length",
  },
];

function feeRuleDraft(rule?: FeeRule): FeeRuleDraft {
  return {
    label: rule?.label ?? "",
    amount: moneyInput(rule?.amount),
    kind: rule && rule.order_under == null ? "length" : "order",
    orderUnder: moneyInput(rule?.order_under),
    minLength: moneyInput(rule?.min_length_in),
    maxLength: moneyInput(rule?.max_length_in),
  };
}

function positive(value: number | null): value is number {
  return value !== null && Number.isFinite(value) && value > 0;
}

/** The rule to save, or null while the row is incomplete. */
function feeRuleWrite(draft: FeeRuleDraft): ManufacturerFeeRuleWrite | null {
  const amount = parsedMoney(draft.amount);
  if (!draft.label.trim() || !positive(amount)) return null;
  if (draft.kind !== "length") {
    const orderUnder = parsedMoney(draft.orderUnder);
    if (!positive(orderUnder)) return null;
    return {
      label: draft.label.trim(),
      amount: normalizedMoney(draft.amount),
      order_under: normalizedMoney(draft.orderUnder),
      min_length_in: null,
      max_length_in: null,
    };
  }
  const min = parsedMoney(draft.minLength);
  const max = parsedMoney(draft.maxLength);
  const minValid = min === null || (Number.isFinite(min) && min >= 0);
  const maxValid = max === null || positive(max);
  if (
    (min === null && max === null) ||
    !minValid ||
    !maxValid ||
    (min !== null && max !== null && min > max)
  ) {
    return null;
  }
  return {
    label: draft.label.trim(),
    amount: normalizedMoney(draft.amount),
    order_under: null,
    min_length_in: min === null ? null : normalizedMoney(draft.minLength),
    max_length_in: max === null ? null : normalizedMoney(draft.maxLength),
  };
}

function feeRuleProblems(draft: FeeRuleDraft): FeeRuleKey[] {
  if (feeRuleWrite(draft) !== null) return [];
  const problems: FeeRuleKey[] = [];
  if (!positive(parsedMoney(draft.amount))) problems.push("amount");
  if (draft.kind === "length") problems.push("minLength", "maxLength");
  else if (!positive(parsedMoney(draft.orderUnder))) problems.push("orderUnder");
  return problems;
}

function ruleWrite(rule: FeeRule): ManufacturerFeeRuleWrite {
  return {
    label: rule.label,
    amount: String(rule.amount),
    order_under: rule.order_under == null ? null : String(rule.order_under),
    min_length_in: rule.min_length_in == null ? null : String(rule.min_length_in),
    max_length_in: rule.max_length_in == null ? null : String(rule.max_length_in),
  };
}

type MinimumOrderKey = "policy" | "threshold" | "fixedFee" | "dropship" | "notes";

const MINIMUM_ORDER_FIELDS: ReadonlyArray<InlineGroupField<MinimumOrderKey>> = [
  {
    key: "policy",
    label: "Minimum order policy",
    kind: "select",
    options: ORDER_FEE_POLICY_OPTIONS,
    className: "col-span-6 sm:col-span-2",
  },
  {
    key: "threshold",
    label: "Minimum order",
    required: true,
    inputMode: "decimal",
    className: "col-span-3 sm:col-span-2",
    hidden: (draft) => draft.policy === "none",
  },
  {
    key: "fixedFee",
    label: "Small order fee",
    required: true,
    inputMode: "decimal",
    className: "col-span-3 sm:col-span-2",
    hidden: (draft) => draft.policy !== "fixed_fee",
  },
  {
    key: "dropship",
    label: "Dropship fee",
    inputMode: "decimal",
    className: "col-span-6 sm:col-span-2",
  },
  { key: "notes", label: "Notes", kind: "textarea" },
];

type BrokenCaseKey = "fee" | "feeMax" | "discount";

const BROKEN_CASE_FIELDS: ReadonlyArray<InlineGroupField<BrokenCaseKey>> = [
  {
    key: "fee",
    label: "Broken-case fee",
    inputMode: "decimal",
    className: "col-span-3 sm:col-span-2",
  },
  {
    key: "feeMax",
    label: "At most per order",
    inputMode: "decimal",
    className: "col-span-3 sm:col-span-2",
  },
  {
    key: "discount",
    label: "Broken-case discount",
    className: "col-span-6 sm:col-span-2",
  },
];

/** Mirrors the backend: amounts above $0, a cap needs a fee and covers it, a discount is a chain. */
function brokenCaseProblems(draft: Record<BrokenCaseKey, string>): BrokenCaseKey[] {
  const fee = parsedMoney(draft.fee);
  const feeMax = parsedMoney(draft.feeMax);
  const problems: BrokenCaseKey[] = [];
  if (fee !== null && !positive(fee)) problems.push("fee");
  if (feeMax !== null && (!positive(feeMax) || fee === null || (positive(fee) && feeMax < fee))) {
    problems.push("feeMax");
  }
  const discount = normalizedChain(draft.discount);
  if (discount !== null && !discountChainValid(discount)) problems.push("discount");
  return problems;
}

function ReadLine({ children, muted = false }: { children: ReactNode; muted?: boolean }) {
  return (
    <span
      className={cn(
        "block text-sm",
        muted ? "font-normal text-muted-foreground" : "font-medium",
      )}
    >
      {children}
    </span>
  );
}

function FeeRuleGroup({
  rule,
  pending,
  editing,
  onEditingChange,
  onSave,
  onRemove,
}: {
  rule?: FeeRule;
  pending: boolean;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  onSave: (rule: ManufacturerFeeRuleWrite) => void;
  onRemove?: () => void;
}) {
  return (
    <div className="flex items-start gap-1">
      <div className="min-w-0 flex-1">
        <InlineFieldGroup
          label={rule ? `${rule.label} fee` : "new fee"}
          fields={FEE_RULE_FIELDS}
          values={feeRuleDraft(rule)}
          invalid={feeRuleProblems}
          saveLabel={rule ? "Save fee" : "Add fee"}
          labelled
          pending={pending}
          editing={editing}
          onEditingChange={onEditingChange}
          onSave={(draft) => {
            const write = feeRuleWrite(draft);
            if (write) onSave(write);
          }}
        >
          <ReadLine muted>{rule ? describeFeeRule(rule) : "New fee"}</ReadLine>
        </InlineFieldGroup>
      </div>
      {rule && onRemove && !editing && (
        <LoadingButton
          type="button"
          variant="ghost"
          size="icon"
          className="size-7 shrink-0 text-muted-foreground hover:text-destructive"
          aria-label={`Remove ${rule.label}`}
          loading={pending}
          onClick={onRemove}
        >
          <Trash2 className="size-3.5" />
        </LoadingButton>
      )}
    </div>
  );
}

/** The manufacturer's order fees, each part click-to-edit in place (USA-1510). */
function OrderFees({
  uuid,
  smallOrderPolicyType,
  smallOrderThreshold,
  smallOrderFee,
  smallOrderFeeNotes,
  dropshipFee,
  feeRules,
  caseTerms,
  mutation,
}: {
  uuid: string;
  smallOrderPolicyType: string | null | undefined;
  smallOrderThreshold: MoneyValue;
  smallOrderFee: MoneyValue;
  smallOrderFeeNotes: string | null | undefined;
  dropshipFee: MoneyValue;
  /** The manufacturer's other fees; omitted for backup sources, which have none. */
  feeRules?: FeeRule[];
  /** The manufacturer's broken-case terms (USA-1496, USA-1510); omitted for backup sources. */
  caseTerms?: ManufacturerCaseTerms;
  mutation?: TermsMutation;
}) {
  const manufacturerUpdate = useUpdateManufacturer(uuid);
  const updateMfr: TermsMutation = mutation ?? manufacturerUpdate;
  const replaceFeeRules = useReplaceManufacturerFeeRules(uuid);
  const updateCaseTerms = useUpdateManufacturerCaseTerms(uuid);
  const [editing, setEditing] = useState<string | null>(null);
  const editor = (name: string) => ({
    editing: editing === name,
    onEditingChange: (open: boolean) => setEditing(open ? name : null),
  });

  const saveMinimumOrder = (draft: Record<MinimumOrderKey, string>) => {
    const policy = orderFeePolicy(draft.policy);
    const hasPolicy = policy !== "none";
    updateMfr.mutate(
      {
        small_order_policy_type: hasPolicy ? policy : null,
        small_order_threshold: hasPolicy ? normalizedMoney(draft.threshold) : null,
        small_order_fee: policy === "fixed_fee" ? normalizedMoney(draft.fixedFee) : null,
        small_order_fee_notes: draft.notes.trim() || null,
        dropship_fee: draft.dropship.trim() ? normalizedMoney(draft.dropship) : null,
      },
      {
        onSuccess: () => {
          toast.success("Order fees updated");
          setEditing(null);
        },
        onError: () => toast.error("Failed to update order fees"),
      },
    );
  };

  const saveRules = (rules: ManufacturerFeeRuleWrite[], done: string) =>
    replaceFeeRules.mutate(rules, {
      onSuccess: () => {
        toast.success(done);
        setEditing(null);
      },
      onError: () => toast.error("Failed to update other fees"),
    });

  const saveBrokenCase = (draft: Record<BrokenCaseKey, string>) => {
    if (caseTerms === undefined) return;
    updateCaseTerms.mutate(
      {
        full_cases_only: caseTerms.full_cases_only,
        broken_case_fee: draft.fee.trim() ? normalizedMoney(draft.fee) : null,
        broken_case_fee_max: draft.feeMax.trim() ? normalizedMoney(draft.feeMax) : null,
        broken_case_discount: normalizedChain(draft.discount),
      },
      {
        onSuccess: () => {
          toast.success("Broken-case terms updated");
          setEditing(null);
        },
        onError: (error) =>
          toast.error(errorDetail(error, "Failed to update broken-case terms")),
      },
    );
  };

  const brokenCaseLines = caseTerms
    ? [describeBrokenCaseFee(caseTerms), describeBrokenCaseDiscount(caseTerms)].filter(
        (line): line is string => line !== null,
      )
    : [];

  return (
    <div className="w-full space-y-2">
      <InlineFieldGroup
        label="order fees"
        fields={MINIMUM_ORDER_FIELDS}
        values={{
          policy: orderFeePolicy(smallOrderPolicyType),
          threshold: moneyInput(smallOrderThreshold),
          fixedFee: moneyInput(smallOrderFee),
          dropship: moneyInput(dropshipFee),
          notes: smallOrderFeeNotes ?? "",
        }}
        invalid={(draft) =>
          (["threshold", "fixedFee", "dropship"] as const).filter(
            (key) => draft[key].trim() !== "" && !positive(parsedMoney(draft[key])),
          )
        }
        saveLabel="Save order fees"
        labelled
        pending={updateMfr.isPending}
        {...editor("minimum")}
        onSave={saveMinimumOrder}
      >
        <ReadLine>
          {describeOrderFees(smallOrderPolicyType, smallOrderThreshold, smallOrderFee)}
        </ReadLine>
        {dropshipFee != null && (
          <ReadLine muted>Dropship fee {formatMoney(dropshipFee)}</ReadLine>
        )}
      </InlineFieldGroup>

      {feeRules !== undefined && (
        <div className="space-y-1">
          {feeRules.map((rule, index) => (
            <FeeRuleGroup
              key={rule.id}
              rule={rule}
              pending={replaceFeeRules.isPending}
              {...editor(`rule-${rule.id}`)}
              onSave={(write) =>
                saveRules(
                  feeRules.map((other, at) => (at === index ? write : ruleWrite(other))),
                  "Fee updated",
                )
              }
              onRemove={() =>
                saveRules(
                  feeRules.filter((_, at) => at !== index).map(ruleWrite),
                  `${rule.label} removed`,
                )
              }
            />
          ))}
          {editing === "new-rule" ? (
            <FeeRuleGroup
              pending={replaceFeeRules.isPending}
              {...editor("new-rule")}
              onSave={(write) =>
                saveRules([...feeRules.map(ruleWrite), write], "Fee added")
              }
            />
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ml-2 h-7 text-xs text-muted-foreground"
              onClick={() => setEditing("new-rule")}
            >
              <Plus className="size-3.5" />
              Add fee
            </Button>
          )}
        </div>
      )}

      {caseTerms !== undefined && (
        <InlineFieldGroup
          label="broken-case terms"
          fields={BROKEN_CASE_FIELDS}
          values={{
            fee: moneyInput(caseTerms.broken_case_fee),
            feeMax: moneyInput(caseTerms.broken_case_fee_max),
            discount: caseTerms.broken_case_discount ?? "",
          }}
          invalid={brokenCaseProblems}
          notice="Replaces the regular discount when an order is below one full case."
          saveLabel="Save broken-case terms"
          labelled
          pending={updateCaseTerms.isPending}
          disabled={caseTerms.full_cases_only}
          {...editor("broken-case")}
          onSave={saveBrokenCase}
        >
          {caseTerms.full_cases_only ? (
            <ReadLine muted>Broken cases: not used while Full cases only is on</ReadLine>
          ) : brokenCaseLines.length > 0 ? (
            brokenCaseLines.map((line) => (
              <ReadLine key={line} muted>
                {line}
              </ReadLine>
            ))
          ) : (
            <ReadLine muted>No broken-case fee or discount</ReadLine>
          )}
        </InlineFieldGroup>
      )}
    </div>
  );
}

/** The purchasing terms a source carries; the preferred source's are the manufacturer's. */
export type PurchasingTerms = Pick<
  components["schemas"]["ManufacturerSourcingOptionRead"],
  | "lead_time_days"
  | "payment_terms"
  | "payment_terms_type"
  | "payment_terms_discount_percent"
  | "payment_terms_discount_days"
  | "payment_terms_net_days"
  | "payment_terms_deposit_percent"
  | "payment_terms_proximo_day"
  | "small_order_policy_type"
  | "small_order_threshold"
  | "small_order_fee"
  | "small_order_fee_notes"
  | "dropship_fee"
  | "tariff_percentage"
  | "skip_spec_credit"
  | "spec_credit_minimum"
  | "freight_booking"
  | "freight_booking_markup_percent"
  | "override_zip_code"
  | "ca_replace_zip_code"
>;

/** Whether the manufacturer's case-packed items sell only by the full case (USA-1496). */
function FullCasesOnlySetting({
  manufacturerUuid,
  caseTerms,
}: {
  manufacturerUuid: string;
  caseTerms: ManufacturerCaseTerms;
}) {
  const update = useUpdateManufacturerCaseTerms(manufacturerUuid);
  const checked =
    update.isPending && update.variables
      ? update.variables.full_cases_only
      : caseTerms.full_cases_only;
  return (
    <SettingCell
      label="Full cases only"
      htmlFor="manufacturer-full-cases-only"
    >
      <Switch
        id="manufacturer-full-cases-only"
        aria-describedby="manufacturer-full-cases-only-hint"
        checked={checked}
        onCheckedChange={(fullCasesOnly) =>
          update.mutate(
            {
              full_cases_only: fullCasesOnly,
              broken_case_fee: caseTerms.broken_case_fee ?? null,
              broken_case_fee_max: caseTerms.broken_case_fee_max ?? null,
              broken_case_discount: caseTerms.broken_case_discount ?? null,
            },
            {
              onError: (error) =>
                toast.error(errorDetail(error, "Could not save full cases only")),
            },
          )
        }
        disabled={update.isPending}
      />
      <span
        id="manufacturer-full-cases-only-hint"
        className="ml-2 text-sm text-muted-foreground"
      >
        Sell case-packed items only by the full case
      </span>
    </SettingCell>
  );
}

/** Whether the manufacturer delivers to homes; off, store checkout offers no shipping to a residential address (USA-1683). */
function HomeDeliveriesSetting({
  manufacturerUuid,
  shipsToResidential,
}: {
  manufacturerUuid: string;
  shipsToResidential: boolean;
}) {
  const update = useUpdateManufacturer(manufacturerUuid);
  const checked =
    update.isPending && update.variables
      ? (update.variables.ships_to_residential as boolean)
      : shipsToResidential;
  return (
    <SettingCell label="Home deliveries" htmlFor="manufacturer-home-deliveries">
      <Switch
        id="manufacturer-home-deliveries"
        checked={checked}
        onCheckedChange={(value) =>
          update.mutate(
            { ships_to_residential: value },
            {
              onError: (error) =>
                toast.error(errorDetail(error, "Could not save home deliveries")),
            },
          )
        }
        disabled={update.isPending}
      />
      {!checked && (
        <span className="ml-2 text-sm text-muted-foreground">
          Checkout blocks home addresses
        </span>
      )}
    </SettingCell>
  );
}

/** Click-to-edit grid of one source's purchasing terms. */
export function PurchasingTermsGrid({
  manufacturerUuid,
  terms,
  defaultFreightMarkup,
  save,
  isSaving,
  mutation,
  feeRules,
  caseTerms,
  paymentMethod,
  cardFee,
  leadTimeCheck,
  shipsToResidential,
}: {
  manufacturerUuid: string;
  terms: PurchasingTerms;
  defaultFreightMarkup: string | null | undefined;
  save: (body: Record<string, unknown>, label: string) => void;
  isSaving: (field: string) => boolean;
  mutation: TermsMutation;
  /** The manufacturer's other fees, shown on its preferred source only. */
  feeRules?: FeeRule[];
  /** Full-case selling and the broken-case fee (USA-1496), shown on its preferred source only. */
  caseTerms?: ManufacturerCaseTerms;
  /** How we pay the manufacturer (USA-1470), shown on its preferred source only; unset is ACH. */
  paymentMethod?: {
    value: ManufacturerPaymentMethod | null | undefined;
    onChange: (value: ManufacturerPaymentMethod) => void;
    pending: boolean;
  };
  /** Whether paying this source by card adds a fee (USA-1650). */
  cardFee?: ReactNode;
  /** The weekly lead-time check switch, beside the lead time on the preferred source (USA-1474). */
  leadTimeCheck?: ReactNode;
  /** Whether the manufacturer delivers to homes (USA-1683), shown on its preferred source only. */
  shipsToResidential?: boolean;
}) {
  const paymentTerms = {
    payment_terms: terms.payment_terms,
    payment_terms_type: terms.payment_terms_type,
    payment_terms_discount_percent: terms.payment_terms_discount_percent,
    payment_terms_discount_days: terms.payment_terms_discount_days,
    payment_terms_net_days: terms.payment_terms_net_days,
    payment_terms_deposit_percent: terms.payment_terms_deposit_percent,
    payment_terms_proximo_day: terms.payment_terms_proximo_day,
  };
  const saveLeadTime = (raw: string) => {
    const trimmed = raw.trim();
    const days = trimmed === "" ? null : Number(trimmed);
    if (days !== null && (!Number.isInteger(days) || days < 0 || days > 365)) {
      toast.error("Lead time must be whole days from 0 to 365");
      return;
    }
    save({ lead_time_days: days }, "lead time");
  };
  const saveTariff = (raw: string) => {
    const trimmed = raw.trim();
    const percent = trimmed === "" ? null : Number(trimmed);
    if (
      percent !== null &&
      (!Number.isFinite(percent) || percent < 0 || percent > 100)
    ) {
      toast.error("Tariff must be a percentage from 0 to 100");
      return;
    }
    save(
      { tariff_percentage: percent === null ? null : String(percent) },
      "tariff",
    );
  };
  const saveFreightBookingMarkup = (raw: string) => {
    const trimmed = raw.trim();
    const percent = trimmed === "" ? null : Number(trimmed);
    if (
      percent !== null &&
      (!Number.isFinite(percent) || percent < 0 || percent > 1000)
    ) {
      toast.error("Freight markup must be a percentage from 0 to 1000");
      return;
    }
    save(
      {
        freight_booking_markup_percent:
          percent === null ? null : String(percent),
      },
      "freight markup",
    );
  };
  const saveSpecCreditMinimum = (raw: string) => {
    const trimmed = raw.replace(/,/g, "").trim();
    const amount = trimmed === "" ? null : Number(trimmed);
    if (amount !== null && (!Number.isFinite(amount) || amount < 0)) {
      toast.error("Spec credit minimum must be a dollar amount of $0 or more");
      return;
    }
    save(
      { spec_credit_minimum: amount === null ? null : String(amount) },
      "spec credit minimum",
    );
  };

  const saveZip = (
    field: "override_zip_code" | "ca_replace_zip_code",
    raw: string,
    label: string,
  ) => {
    const zip = raw.trim();
    if (zip && !/^\d{5}$/.test(zip)) {
      toast.error("Enter a 5-digit ZIP");
      return;
    }
    save({ [field]: zip || null }, label);
  };

  return (
    <div className="-mb-px -mr-px grid grid-cols-1 sm:grid-cols-2">
      <SettingCell label="Lead time">
        <POInlineEditableField
          value={terms.lead_time_days}
          displayValue={
            terms.lead_time_days != null
              ? `${terms.lead_time_days} business ${terms.lead_time_days === 1 ? "day" : "days"}`
              : undefined
          }
          kind="number"
          placeholder="Not set"
          ariaLabel="Edit lead time in business days"
          pending={isSaving("lead_time_days")}
          showEditIcon
          buttonClassName={cn(
            "-ml-2 font-medium",
            terms.lead_time_days == null && "text-muted-foreground",
          )}
          inputClassName="w-24"
          onConfirm={saveLeadTime}
        />
      </SettingCell>
      {leadTimeCheck}
      <SettingCell label="Payment terms">
        <PaymentTermsField
          uuid={manufacturerUuid}
          mutation={mutation}
          terms={paymentTerms}
        />
      </SettingCell>
      {paymentMethod && (
        <SettingCell label="How we pay">
          <ReviewChoiceGroup
            label="How we pay"
            value={paymentMethod.value ?? "ach"}
            options={PAYMENT_METHOD_OPTIONS}
            // Five methods fit one line in the half-width cell (USA-1183).
            optionClassName="px-2"
            onChange={paymentMethod.onChange}
            disabled={paymentMethod.pending}
          />
        </SettingCell>
      )}
      {cardFee && (
        <SettingCell label="Card fee" className="sm:col-span-2">
          {cardFee}
        </SettingCell>
      )}
      <SettingCell label="Order fees" className="sm:col-span-2">
        <OrderFees
          uuid={manufacturerUuid}
          mutation={mutation}
          smallOrderPolicyType={terms.small_order_policy_type}
          smallOrderThreshold={terms.small_order_threshold}
          smallOrderFee={terms.small_order_fee}
          smallOrderFeeNotes={terms.small_order_fee_notes}
          dropshipFee={terms.dropship_fee}
          feeRules={feeRules}
          caseTerms={caseTerms}
        />
      </SettingCell>
      <SettingCell label="Tariff">
        <POInlineEditableField
          value={
            terms.tariff_percentage != null
              ? String(Number(terms.tariff_percentage))
              : ""
          }
          displayValue={
            terms.tariff_percentage != null
              ? formatPercent(terms.tariff_percentage)
              : undefined
          }
          kind="number"
          placeholder="None"
          ariaLabel="Edit tariff percentage"
          pending={isSaving("tariff_percentage")}
          showEditIcon
          buttonClassName={cn(
            "-ml-2 font-medium",
            terms.tariff_percentage == null && "text-muted-foreground",
          )}
          inputClassName="w-24"
          onConfirm={saveTariff}
        />
      </SettingCell>
      <SettingCell label="Spec credit minimum">
        <POInlineEditableField
          value={formatSpecCreditMinimum(terms.spec_credit_minimum)}
          displayValue={
            terms.spec_credit_minimum != null
              ? formatMoney(terms.spec_credit_minimum)
              : undefined
          }
          kind="money"
          placeholder="$500 default"
          ariaLabel="Edit spec credit minimum"
          pending={isSaving("spec_credit_minimum")}
          showEditIcon
          buttonClassName={cn(
            "-ml-2 font-medium",
            terms.spec_credit_minimum == null &&
              "font-normal text-muted-foreground",
          )}
          inputClassName="w-28"
          onConfirm={saveSpecCreditMinimum}
        />
      </SettingCell>
      <SettingCell
        label="Skip spec credit"
        htmlFor="manufacturer-skip-spec-credit"
      >
        <Switch
          id="manufacturer-skip-spec-credit"
          checked={terms.skip_spec_credit ?? false}
          onCheckedChange={(checked) =>
            save({ skip_spec_credit: checked }, "spec credit setting")
          }
          disabled={isSaving("skip_spec_credit")}
        />
      </SettingCell>
      {caseTerms && (
        <FullCasesOnlySetting
          manufacturerUuid={manufacturerUuid}
          caseTerms={caseTerms}
        />
      )}
      <SettingCell label="Ships from">
        <POInlineEditableField
          value={terms.override_zip_code ?? ""}
          displayValue={terms.override_zip_code || undefined}
          placeholder="Product ZIP"
          ariaLabel="Edit ship-from ZIP"
          pending={isSaving("override_zip_code")}
          showEditIcon
          buttonClassName={cn(
            "-ml-2 font-medium",
            !terms.override_zip_code && "font-normal text-muted-foreground",
          )}
          inputClassName="w-24"
          onConfirm={(raw) =>
            saveZip("override_zip_code", raw, "ship-from ZIP")
          }
        />
      </SettingCell>
      <SettingCell label="CA orders ship from">
        <POInlineEditableField
          value={terms.ca_replace_zip_code ?? ""}
          displayValue={terms.ca_replace_zip_code || undefined}
          placeholder="Same"
          ariaLabel="Edit ship-from ZIP for California orders"
          pending={isSaving("ca_replace_zip_code")}
          showEditIcon
          buttonClassName={cn(
            "-ml-2 font-medium",
            !terms.ca_replace_zip_code && "font-normal text-muted-foreground",
          )}
          inputClassName="w-24"
          onConfirm={(raw) =>
            saveZip("ca_replace_zip_code", raw, "California ship-from ZIP")
          }
        />
      </SettingCell>
      <SettingCell label="Freight booking" className="sm:col-span-2">
        <ReviewChoiceGroup
          label="Who books freight"
          value={terms.freight_booking ?? "auto"}
          options={FREIGHT_BOOKING_OPTIONS}
          onChange={(value) =>
            save({ freight_booking: value }, "freight booking")
          }
          disabled={isSaving("freight_booking")}
        />
      </SettingCell>
      {terms.freight_booking === "manufacturer" && (
        <SettingCell label="Freight markup">
          <POInlineEditableField
            value={
              terms.freight_booking_markup_percent != null
                ? String(Number(terms.freight_booking_markup_percent))
                : ""
            }
            displayValue={
              terms.freight_booking_markup_percent != null
                ? formatPercent(terms.freight_booking_markup_percent)
                : undefined
            }
            kind="number"
            placeholder={
              defaultFreightMarkup != null
                ? `${formatPercent(defaultFreightMarkup)} default`
                : "Default"
            }
            ariaLabel="Edit freight markup when the manufacturer books"
            pending={isSaving("freight_booking_markup_percent")}
            showEditIcon
            buttonClassName={cn(
              "-ml-2 font-medium",
              terms.freight_booking_markup_percent == null &&
                "font-normal text-muted-foreground",
            )}
            inputClassName="w-24"
            onConfirm={saveFreightBookingMarkup}
          />
        </SettingCell>
      )}
      {shipsToResidential !== undefined && (
        <HomeDeliveriesSetting
          manufacturerUuid={manufacturerUuid}
          shipsToResidential={shipsToResidential}
        />
      )}
    </div>
  );
}
